function initializeMenu(): void {
  const button = document.querySelector<HTMLButtonElement>('[data-menu-button]')
  const nav = document.querySelector<HTMLElement>('[data-mobile-nav]')
  if (!button || !nav) return

  const close = (): void => {
    button.setAttribute('aria-expanded', 'false')
    nav.classList.remove('is-open')
  }

  button.addEventListener('click', () => {
    const open = button.getAttribute('aria-expanded') === 'true'
    button.setAttribute('aria-expanded', String(!open))
    nav.classList.toggle('is-open', !open)
  })
  nav.querySelectorAll('a').forEach(link => link.addEventListener('click', close))
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') close()
  })
}

function initializeHeader(): void {
  const header = document.querySelector<HTMLElement>('[data-header]')
  if (!header) return
  const update = (): void => header.classList.toggle('is-scrolled', window.scrollY > 24)
  update()
  window.addEventListener('scroll', update, { passive: true })
}

function initializeReveals(): void {
  const items = Array.from(document.querySelectorAll<HTMLElement>('.reveal'))
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach(item => item.classList.add('is-visible'))
    return
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return
      entry.target.classList.add('is-visible')
      observer.unobserve(entry.target)
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -48px' })
  items.forEach(item => observer.observe(item))
}

function initializeLoop(): void {
  const steps = Array.from(document.querySelectorAll<HTMLElement>('[data-step]'))
  const panels = Array.from(document.querySelectorAll<HTMLElement>('[data-panel]'))
  if (!steps.length || !panels.length) return

  const loopSection = document.querySelector<HTMLElement>('[data-loop]')
  const isAd2 = document.documentElement.getAttribute('data-ad') === '2'
  const isMobile = (): boolean => window.matchMedia('(max-width: 900px)').matches

  // Single source of truth: every activation (hover, click, or scroll) goes
  // through this one function so the active step label and phone panel can
  // never disagree.
  let manual = false
  const activate = (name: string): void => {
    steps.forEach(step => step.classList.toggle('is-active', step.dataset.step === name))
    panels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.panel === name))
  }

  steps.forEach(step => {
    const name = step.dataset.step
    if (!name) return

    step.addEventListener('click', () => {
      manual = true
      activate(name)
    })

    // Desktop only, and never on AD2 (scroll is the sole driver there to
    // avoid the hover/scroll dual-driver flicker).
    if (isAd2) return
    step.addEventListener('mouseenter', () => {
      if (isMobile()) return
      activate(name)
    })
    step.addEventListener('focus', () => {
      if (isMobile()) return
      activate(name)
    })
  })

  // Scroll/IO drives state only while the user hasn't manually picked a step,
  // and never on mobile (click/tap only there, per the AD2 spec and to avoid
  // fighting a stacked layout everywhere else).
  const observer = new IntersectionObserver(entries => {
    if (manual || isMobile()) return
    const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
    const name = (visible?.target as HTMLElement | undefined)?.dataset.step
    if (name) activate(name)
  }, { threshold: [0.35, 0.6, 0.85] })
  steps.forEach(step => observer.observe(step))

  // Clear the manual override once the loop section leaves the viewport, so
  // scroll resumes driving state on the next visit.
  if (loopSection) {
    const manualReset = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) manual = false
      })
    }, { threshold: 0 })
    manualReset.observe(loopSection)
  }
}

function initializeHeroVideo(): void {
  const video = document.querySelector<HTMLVideoElement>('[data-hero-video]')
  if (!video) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.pause()
    return
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) void video.play().catch(() => undefined)
      else video.pause()
    })
  }, { threshold: 0.1 })
  observer.observe(video)
}

export function initializeApp(): void {
  initializeMenu()
  initializeHeader()
  initializeReveals()
  initializeLoop()
  initializeHeroVideo()
}
